import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft } from "lucide-react";

// Status / decision / offer display helpers shared with list page
const STATUS_META = {
  DRAFT:         { label: "Draft",         color: "bg-slate-500/15 text-slate-400  border-slate-500/30" },
  SUBMITTED:     { label: "Submitted",     color: "bg-blue-500/15   text-blue-400   border-blue-500/30" },
  UNDER_REVIEW:  { label: "Under Review",  color: "bg-amber-500/15  text-amber-400  border-amber-500/30" },
  DECISION_MADE: { label: "Decision Made", color: "bg-violet-500/15 text-violet-400 border-violet-500/30" },
  WITHDRAWN:     { label: "Withdrawn",     color: "bg-slate-500/15  text-slate-400  border-slate-500/30" },
  CANCELLED:     { label: "Cancelled",     color: "bg-red-500/15    text-red-400    border-red-500/30"   },
};

const DECISION_META = {
  ADMIT:    { label: "Admitted",   color: "text-emerald-400", pill: "bg-emerald-500/15 border-emerald-500/30 text-emerald-400" },
  REJECT:   { label: "Rejected",   color: "text-red-400",     pill: "bg-red-500/15     border-red-500/30     text-red-400"     },
  WAITLIST: { label: "Waitlisted", color: "text-amber-400",   pill: "bg-amber-500/15   border-amber-500/30   text-amber-400"   },
  DEFER:    { label: "Deferred",   color: "text-blue-400",    pill: "bg-blue-500/15    border-blue-500/30    text-blue-400"    },
};

const OFFER_META = {
  ISSUED:   { label: "Offer Issued",   pill: "bg-amber-500/15   border-amber-500/30   text-amber-400"   },
  ACCEPTED: { label: "Offer Accepted", pill: "bg-emerald-500/15 border-emerald-500/30 text-emerald-400" },
  DECLINED: { label: "Offer Declined", pill: "bg-red-500/15     border-red-500/30     text-red-400"     },
  EXPIRED:  { label: "Expired",        pill: "bg-slate-500/15   border-slate-500/30   text-slate-400"   },
  REVOKED:  { label: "Revoked",        pill: "bg-slate-500/15   border-slate-500/30   text-slate-400"   },
};

function SectionCard({ title, badge, children, source = "CANONICAL_PG", dashed = false }) {
  const borderCls = dashed
    ? "border-dashed border-amber-500/30"
    : "border-border";
  return (
    <div className={`border ${borderCls} rounded-xl p-5 mb-5`}>
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">{title}</h3>
        {badge && (
          <span className={`px-2 py-0.5 text-xs font-semibold tracking-widest rounded border ${badge}`}>
            {source === "CANONICAL_PG" ? "CANONICAL PG" : source === "NOT_CONNECTED" ? "NOT CONNECTED" : "TRANSITIONAL"}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      <p className={`text-sm font-medium ${mono ? "font-mono" : ""}`}>
        {value ?? <span className="text-muted-foreground">—</span>}
      </p>
    </div>
  );
}

export default function ApplicationDetail() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/core/applications/${applicationId}`)
      .then(r => { if (!cancelled) setApp(r.data); })
      .catch(e => {
        if (!cancelled) {
          const status = e?.response?.status;
          setError(status === 404 ? "Application not found." : (e?.response?.data?.detail || "Failed to load application."));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [applicationId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center"
           data-testid="application-detail-loading">
        <p className="text-muted-foreground text-sm">Loading application…</p>
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4"
           data-testid="application-detail-error">
        <p className="text-destructive text-sm">{error || "Application not found."}</p>
        <button className="text-sm text-muted-foreground hover:text-foreground" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>
    );
  }

  const statusMeta = STATUS_META[app.status] || { label: app.status, color: "bg-slate-500/15 text-slate-400 border-slate-500/30" };
  const latestDecision = app.decisions?.[app.decisions.length - 1];
  const decisionMeta = latestDecision ? (DECISION_META[latestDecision.decision] || { label: latestDecision.decision, pill: "bg-slate-500/15 border-slate-500/30 text-slate-400" }) : null;
  const offerMeta = app.offer ? (OFFER_META[app.offer.status] || { label: app.offer.status, pill: "bg-slate-500/15 border-slate-500/30 text-slate-400" }) : null;

  return (
    <div className="min-h-screen bg-background" data-testid="application-detail-page">
      <div className="max-w-screen-xl mx-auto px-6 py-8">

        {/* Back */}
        <button
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          onClick={() => navigate("/admissions/applications")}
          data-testid="application-detail-back"
        >
          <ArrowLeft size={14} />
          Applications
        </button>

        {/* Header */}
        <div className="mb-8">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
            RECRUITMENT &amp; ADMISSIONS · APPLICATION DETAIL
          </p>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-4xl font-semibold tracking-tight"
                  data-testid="application-detail-applicant-name">
                {app.applicant?.display_name || "Unknown Applicant"}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <span className={`inline-flex items-center px-2.5 py-1 rounded border text-xs font-medium ${statusMeta.color}`}
                      data-testid="application-detail-status">
                  {statusMeta.label}
                </span>
                {app.cycle_code && (
                  <span className="text-sm text-muted-foreground">{app.cycle_code}</span>
                )}
              </div>
            </div>
            <span className="px-2.5 py-1 text-xs font-semibold tracking-widest bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded mt-1">
              CANONICAL PG
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-0">

            {/* Overview */}
            <SectionCard title="Application Overview" badge="bg-emerald-500/15 border-emerald-500/30 text-emerald-400">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Applicant" value={app.applicant?.display_name} />
                <Field label="Given Name" value={app.applicant?.given_name} />
                <Field label="Family Name" value={app.applicant?.family_name} />
                <Field label="Program" value={app.program?.title} />
                <Field label="Degree Type" value={app.program?.degree_type} />
                <Field label="Cycle Code" value={app.cycle_code} mono />
                <Field label="Application Status" value={statusMeta.label} />
                <Field label="Submitted" value={app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : null} />
                <Field label="Application ID" value={app.id?.slice(0, 8) + "…"} mono />
              </div>
            </SectionCard>

            {/* Application Versioning */}
            <SectionCard title="Application Versioning" badge="bg-emerald-500/15 border-emerald-500/30 text-emerald-400">
              {app.current_version ? (
                <div className="grid grid-cols-3 gap-4">
                  <Field label="Current Version" value={`v${app.current_version.version_no}`} />
                  <Field label="Version ID" value={app.current_version.id?.slice(0, 8) + "…"} mono />
                  <Field label="Captured At" value={app.current_version.captured_at ? new Date(app.current_version.captured_at).toLocaleDateString() : null} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No version snapshot captured yet.</p>
              )}
              <p className="text-xs text-muted-foreground mt-3">
                Application versions are append-only. Each change captures a snapshot. This preserves
                canonical history without mutation (DB trigger enforces immutability).
              </p>
            </SectionCard>

            {/* Admission Decision */}
            <SectionCard
              title="Admission Decision"
              badge={latestDecision ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-400" : "bg-slate-500/15 border-slate-500/30 text-slate-400"}
              source={latestDecision ? "CANONICAL_PG" : "NOT_CONNECTED"}
            >
              {latestDecision ? (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Decision</p>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded border text-xs font-semibold ${decisionMeta?.pill}`}
                          data-testid="application-detail-decision">
                      {decisionMeta?.label || latestDecision.decision}
                    </span>
                  </div>
                  <Field label="Decision Date" value={latestDecision.decided_at ? new Date(latestDecision.decided_at).toLocaleDateString() : null} />
                  <Field label="Decision ID" value={latestDecision.id?.slice(0, 8) + "…"} mono />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="application-detail-no-decision">
                  No admission decision recorded yet.
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-3">
                Decision is canonically separate from Application status. Values: ADMIT, REJECT, WAITLIST, DEFER.
              </p>
            </SectionCard>

            {/* Offer */}
            <SectionCard
              title="Offer"
              badge={app.offer ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-400" : "bg-slate-500/15 border-slate-500/30 text-slate-400"}
              source={app.offer ? "CANONICAL_PG" : "NOT_CONNECTED"}
            >
              {app.offer ? (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Offer Status</p>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded border text-xs font-semibold ${offerMeta?.pill}`}
                          data-testid="application-detail-offer-status">
                      {offerMeta?.label || app.offer.status}
                    </span>
                  </div>
                  <Field label="Issued" value={app.offer.issued_at ? new Date(app.offer.issued_at).toLocaleDateString() : null} />
                  <Field label="Expires" value={app.offer.expires_at ? new Date(app.offer.expires_at).toLocaleDateString() : null} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="application-detail-no-offer">
                  No offer issued yet.
                </p>
              )}
            </SectionCard>

            {/* Offer Acceptance */}
            <SectionCard
              title="Offer Acceptance"
              badge={app.offer_acceptance ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-400" : "bg-slate-500/15 border-slate-500/30 text-slate-400"}
              source={app.offer_acceptance ? "CANONICAL_PG" : "NOT_CONNECTED"}
            >
              {app.offer_acceptance ? (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Acceptance ID" value={app.offer_acceptance.id?.slice(0, 8) + "…"} mono />
                  <Field label="Accepted On" value={new Date(app.offer_acceptance.accepted_at).toLocaleDateString()} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="application-detail-no-acceptance">
                  {app.offer ? "Offer has not been accepted yet." : "No offer to accept."}
                </p>
              )}
            </SectionCard>

          </div>

          {/* Right sidebar */}
          <div className="space-y-0">

            {/* Student Handoff */}
            <SectionCard
              title="Student Handoff"
              badge={
                app.student_handoff?.status === "CONNECTED"
                  ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-400"
                  : "bg-slate-500/15 border-slate-500/30 text-slate-400"
              }
              source={app.student_handoff?.status === "CONNECTED" ? "CANONICAL_PG" : "NOT_CONNECTED"}
            >
              <div className="space-y-3" data-testid="application-detail-handoff">
                {app.student_handoff?.status === "CONNECTED" ? (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                      <span className="text-sm font-medium text-emerald-400">Connected</span>
                    </div>
                    <Field label="Student Number" value={app.student_handoff.student_number} mono />
                    <Field label="Student ID" value={app.student_handoff.student_id?.slice(0, 8) + "…"} mono />
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-slate-500"></div>
                      <span className="text-sm font-medium text-muted-foreground">Not Connected</span>
                    </div>
                  </>
                )}
                <p className="text-xs text-muted-foreground">{app.student_handoff?.note}</p>
              </div>
            </SectionCard>

            {/* Lifecycle summary card */}
            <div className="border border-border rounded-xl p-5">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                Admission Lifecycle
              </h3>
              <div className="space-y-3">
                {[
                  { label: "Application", value: STATUS_META[app.status]?.label || app.status, active: true },
                  { label: "Version", value: app.current_version ? `v${app.current_version.version_no}` : "None", active: !!app.current_version },
                  { label: "Decision", value: latestDecision ? (DECISION_META[latestDecision.decision]?.label || latestDecision.decision) : "Pending", active: !!latestDecision },
                  { label: "Offer", value: app.offer ? (OFFER_META[app.offer.status]?.label || app.offer.status) : "None", active: !!app.offer },
                  { label: "Acceptance", value: app.offer_acceptance ? "Accepted" : "Pending", active: !!app.offer_acceptance },
                  { label: "Student", value: app.student_handoff?.status === "CONNECTED" ? "Connected" : "Not Connected", active: app.student_handoff?.status === "CONNECTED" },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${step.active ? "bg-emerald-400" : "bg-slate-700"}`}></div>
                    <div className="flex items-center justify-between flex-1 min-w-0">
                      <span className="text-xs text-muted-foreground">{step.label}</span>
                      <span className={`text-xs font-medium truncate ml-2 ${step.active ? "text-foreground" : "text-muted-foreground"}`}>
                        {step.value}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
